import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Plus,
  Search,
  Phone,
  MapPin,
  User,
  GripVertical,
  AlertCircle,
  MessageCircle,
  RefreshCw,
  Calendar,
  PhoneCall,
} from 'lucide-react';
import { toast } from 'sonner';
import LeadDialog from '../components/dialogs/LeadDialog';
import LeadDetailsDialog from '../components/dialogs/LeadDetailsDialog';
import StatusChangeDialog from '../components/dialogs/StatusChangeDialog';
import NextActionDialog from '../components/dialogs/NextActionDialog';
import CallRemarkDialog from '../components/dialogs/CallRemarkDialog';

// Auto-refresh interval (30 seconds)
const AUTO_REFRESH_INTERVAL = 30000;

// Date filter presets
const DATE_PRESETS = [
  { value: 'all', label: 'All Time' },
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_7_days', label: 'Last 7 Days' },
  { value: 'this_month', label: 'This Month' },
];

// WhatsApp helper functions
const getWhatsAppMessage = (lead, status) => {
  const meetingData = lead?.virtualMeetData || {};
  const meetingDateTime = meetingData.meetingDateTime 
    ? new Date(meetingData.meetingDateTime).toLocaleString() 
    : '[DATE/TIME]';
  const meetLink = meetingData.googleMeetLink || '[MEET_LINK]';
  
  if (status === 'Virtual Meet') {
    return `Hello ${lead?.name || ''},

Thanks for the information you have given during qualification. I am eager to meet you in Google Meet.

Please join using the link below:
${meetLink}

Date & Time: ${meetingDateTime}

Looking forward to meeting you!

Best regards,
Servall Team`;
  }
  
  if (status === 'Awaited Visit') {
    return `Hello ${lead?.name || ''},

Thank you for the time for Google Meet. You asked important questions and showed interest.

I would like to confirm your visit. Please share your preferred date and time.

Best regards,
Servall Team`;
  }
  
  return `Hello ${lead?.name || ''},

Thank you for your interest in Servall. We would like to follow up with you.

Best regards,
Servall Team`;
};

const openWhatsApp = (mobile, message) => {
  let cleanNumber = mobile?.replace(/\D/g, '') || '';
  if (cleanNumber.length === 10) {
    cleanNumber = '91' + cleanNumber;
  }
  const encodedMessage = encodeURIComponent(message);
  window.open(`https://wa.me/${cleanNumber}?text=${encodedMessage}`, '_blank');
};

const LEAD_STATUSES = [
  'New Lead',
  'Uncontacted',
  'Qualified Lead',
  'Virtual Meet',
  'Awaited Visit',
  'Awaited LOI',
  'Closed LOI',
  'Outlet Opened',
  'Nurture',
  'Dead',
];

// Valid status transitions
const VALID_TRANSITIONS = {
  "New Lead": ["Uncontacted", "Qualified Lead", "Nurture", "Dead"],
  "Uncontacted": ["New Lead", "Qualified Lead", "Nurture", "Dead"],
  "Qualified Lead": ["Virtual Meet", "Nurture", "Dead"],
  "Virtual Meet": ["Awaited Visit", "Nurture", "Dead"],
  "Awaited Visit": ["Awaited LOI", "Nurture", "Dead"],
  "Awaited LOI": ["Closed LOI", "Nurture", "Dead"],
  "Closed LOI": ["Outlet Opened", "Dead"],
  "Outlet Opened": [],
  "Nurture": ["New Lead", "Qualified Lead", "Dead"],
  "Dead": ["New Lead", "Nurture"]
};

const getStatusColor = (status) => {
  const colors = {
    'New Lead': 'bg-blue-500',
    'Uncontacted': 'bg-gray-500',
    'Qualified Lead': 'bg-indigo-500',
    'Virtual Meet': 'bg-purple-500',
    'Awaited Visit': 'bg-orange-500',
    'Awaited LOI': 'bg-yellow-500',
    'Closed LOI': 'bg-teal-500',
    'Outlet Opened': 'bg-green-500',
    'Nurture': 'bg-cyan-500',
    'Dead': 'bg-red-500',
  };
  return colors[status] || 'bg-gray-500';
};

const LeadsBoardPage = () => {
  const { api, isManager } = useAuth();
  const [leads, setLeads] = useState([]);
  const [executives, setExecutives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedExecutive, setSelectedExecutive] = useState('all');
  const [datePreset, setDatePreset] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [totalLeads, setTotalLeads] = useState(0);
  const refreshIntervalRef = useRef(null);
  
  // Status change state
  const [statusChangeDialog, setStatusChangeDialog] = useState({
    open: false,
    lead: null,
    targetStatus: null
  });

  // Call remark dialog state
  const [callRemarkDialog, setCallRemarkDialog] = useState({
    open: false,
    lead: null
  });

  // Next-action dialog (mandatory popup after intermediate status change)
  const [nextActionDialog, setNextActionDialog] = useState({
    open: false,
    lead: null,
    statusContext: null,
  });

  // Lead-score filter (all / hot / warm / cold)
  const [scoreFilter, setScoreFilter] = useState('all');

  // Handle Quick Call - opens phone dialer, logs call initiation, and shows remark dialog
  const handleQuickCall = async (e, lead) => {
    e.stopPropagation();
    
    try {
      // Log call initiation via API
      const response = await api.post('/calls/initiate', {
        leadId: lead.id,
        phoneNumber: lead.mobile
      });
      
      // Open phone dialer using tel link
      window.location.href = response.data.telLink || `tel:${lead.mobile}`;
      
      // Show remark dialog after a short delay (to let dialer open)
      setTimeout(() => {
        setCallRemarkDialog({ open: true, lead, callId: response.data.callId });
      }, 500);
    } catch (err) {
      // Fallback - still open dialer even if API fails
      window.location.href = `tel:${lead.mobile}`;
      setTimeout(() => {
        setCallRemarkDialog({ open: true, lead });
      }, 500);
    }
  };

  // Handle call log submission
  const handleCallLogSubmit = (callLog) => {
    // Update lead in local state with new CNR count
    setLeads(prev => prev.map(l => 
      l.id === callLog.leadId 
        ? { ...l, cnrCount: (l.cnrCount || 0) + 1, lastCalledAt: callLog.calledAt, lastCallOutcome: callLog.outcome }
        : l
    ));
  };

  // Fetch all leads with pagination handling
  const fetchLeads = useCallback(async (showRefreshIndicator = false) => {
    try {
      if (showRefreshIndicator) setIsRefreshing(true);
      
      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (selectedExecutive && selectedExecutive !== 'all') {
        params.append('assignee', selectedExecutive);
      }
      if (datePreset && datePreset !== 'all') {
        params.append('date_preset', datePreset);
      }
      // Use fast kanban endpoint - stage counts + cards per stage
      params.append('cards_per_stage', '50');

      const response = await api.get(`/leads/kanban-board?${params.toString()}`);
      const stagesData = response.data.stages || [];
      const total = response.data.total || 0;

      // Flatten all cards into leads array (preserving stage info)
      const newLeads = stagesData.flatMap(s => s.cards || []);

      setLeads(newLeads);
      setTotalLeads(total);
    } catch (err) {
      console.error('Failed to load leads:', err);
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
      setIsRefreshing(false);
    }
  }, [api, searchTerm, selectedExecutive, datePreset]);

  const fetchExecutives = useCallback(async () => {
    if (!isManager) return;
    try {
      const response = await api.get('/users/executives');
      setExecutives(response.data || []);
    } catch (err) {
      console.error('Failed to load executives');
    }
  }, [api, isManager]);

  // Initial fetch
  useEffect(() => {
    fetchLeads();
    fetchExecutives();
  }, [fetchLeads, fetchExecutives]);

  // Auto-refresh for real-time sync
  useEffect(() => {
    // Set up auto-refresh interval
    refreshIntervalRef.current = setInterval(() => {
      fetchLeads(true); // Pass true to show refresh indicator
    }, AUTO_REFRESH_INTERVAL);

    // Cleanup on unmount
    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [fetchLeads]);

  // Manual refresh function
  const handleManualRefresh = () => {
    fetchLeads(true);
  };

  const handleDragStart = (e, lead) => {
    e.dataTransfer.setData('lead', JSON.stringify(lead));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e, targetStatus) => {
    e.preventDefault();
    const leadData = e.dataTransfer.getData('lead');
    if (!leadData) return;
    
    const lead = JSON.parse(leadData);
    const currentStatus = lead.status;
    
    // Check if this is a valid transition
    const validTransitions = VALID_TRANSITIONS[currentStatus] || [];
    
    if (currentStatus === targetStatus) {
      return; // No change needed
    }
    
    if (!validTransitions.includes(targetStatus)) {
      toast.error(`Cannot move from "${currentStatus}" to "${targetStatus}". Allowed: ${validTransitions.join(', ') || 'None'}`);
      return;
    }
    
    // Open status change dialog with required fields
    setStatusChangeDialog({
      open: true,
      lead: lead,
      targetStatus: targetStatus
    });
  };

  const handleStatusChangeSuccess = (updatedLead) => {
    const dialogLead = statusChangeDialog.lead;
    setStatusChangeDialog({ open: false, lead: null, targetStatus: null });
    fetchLeads();
    // Jul 2025 — if backend flags this status as a Next-Action trigger, open the popup
    if (updatedLead && updatedLead.nextActionRequired) {
      setNextActionDialog({
        open: true,
        lead: updatedLead,
        statusContext: updatedLead.status,
      });
    } else if (dialogLead) {
      // Backend didn't return updatedLead (rare). Fall back to trigger-set check using dialog state.
      const triggers = ['Qualified Lead', 'Virtual Meet', 'Awaited Visit', 'Awaited LOI', 'Nurture'];
      if (triggers.includes(statusChangeDialog.targetStatus)) {
        setNextActionDialog({
          open: true,
          lead: dialogLead,
          statusContext: statusChangeDialog.targetStatus,
        });
      }
    }
  };

  const getLeadsByStatus = (status) => {
    let arr = leads.filter((lead) => lead.status === status);
    if (scoreFilter && scoreFilter !== 'all') {
      arr = arr.filter((lead) => lead.leadScore === scoreFilter);
    }
    return arr;
  };

  const handleLeadCreated = () => {
    setShowCreateDialog(false);
    fetchLeads();
  };

  const handleLeadUpdated = () => {
    setSelectedLead(null);
    fetchLeads();
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="board-loading">
        <div className="flex items-center justify-between">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-32" />
        </div>
        <div className="flex gap-6 overflow-x-auto pb-4">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="min-w-[300px] h-[500px]" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="leads-board">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">
            Leads Board
          </h1>
          <p className="text-muted-foreground">
            Drag and drop leads to change their status (workflows will open automatically)
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Lead Count & Sync Status */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Badge variant="outline" className="font-normal">
              {leads.length} / {totalLeads} leads
            </Badge>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={handleManualRefresh}
              disabled={isRefreshing}
              className="h-8 px-2"
              data-testid="refresh-btn"
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          <Button onClick={() => setShowCreateDialog(true)} data-testid="create-lead-btn">
            <Plus className="mr-2 h-4 w-4" />
            Add Lead
          </Button>
        </div>
      </div>

      {/* Date Filter & Info Banner */}
      <div className="flex flex-wrap items-center gap-4">
        <Select value={datePreset} onValueChange={setDatePreset}>
          <SelectTrigger className="w-[150px]" data-testid="date-filter">
            <Calendar className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Date Range" />
          </SelectTrigger>
          <SelectContent>
            {DATE_PRESETS.map(preset => (
              <SelectItem key={preset.value} value={preset.value}>
                {preset.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2 p-2 px-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-700">
          <AlertCircle className="h-4 w-4" />
          <span>Click <PhoneCall className="h-3 w-3 inline mx-1" /> to call & log remarks</span>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search leads..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="search-input"
          />
        </div>
        {isManager && (
          <Select value={selectedExecutive} onValueChange={setSelectedExecutive}>
            <SelectTrigger className="w-[200px]" data-testid="executive-filter">
              <SelectValue placeholder="All Executives" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Executives</SelectItem>
              {executives.map((exec) => (
                <SelectItem key={exec.id} value={exec.id}>
                  {exec.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {/* Lead Score Filter (Jul 2025) */}
        <Select value={scoreFilter} onValueChange={setScoreFilter}>
          <SelectTrigger className="w-[170px]" data-testid="score-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Scores</SelectItem>
            <SelectItem value="hot">🔥 Hot only</SelectItem>
            <SelectItem value="warm">🪵 Warm only</SelectItem>
            <SelectItem value="cold">❄️ Cold only</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Kanban Board */}
      <div className="flex gap-6 overflow-x-auto pb-4 kanban-board">
        {LEAD_STATUSES.map((status) => {
          const statusLeads = getLeadsByStatus(status);
          return (
            <div
              key={status}
              className="min-w-[300px] flex-shrink-0"
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, status)}
              data-testid={`column-${status.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="bg-muted/30 rounded-lg p-4 h-[calc(100vh-16rem)]">
                {/* Column Header */}
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${getStatusColor(status)}`} />
                    <h3 className="font-semibold text-sm">{status}</h3>
                  </div>
                  <Badge variant="secondary" className="text-xs">
                    {statusLeads.length}
                  </Badge>
                </div>

                {/* Lead Cards */}
                <div className="space-y-3 overflow-y-auto h-[calc(100%-3rem)] pr-1">
                  {statusLeads.map((lead) => {
                    // Calculate activity status for color coding
                    const getActivityColor = () => {
                      if (!lead.updatedAt) return 'border-l-4 border-l-gray-400'; // No activity - Gray
                      
                      const lastUpdate = new Date(lead.updatedAt);
                      const now = new Date();
                      const daysDiff = Math.floor((now - lastUpdate) / (1000 * 60 * 60 * 24));
                      
                      if (daysDiff <= 3) return 'border-l-4 border-l-green-500'; // Active - Green
                      if (daysDiff <= 7) return 'border-l-4 border-l-yellow-500'; // Follow-up pending - Yellow
                      if (daysDiff <= 15) return 'border-l-4 border-l-orange-500'; // Getting stale - Orange
                      return 'border-l-4 border-l-red-500'; // Inactive - Red
                    };
                    
                    return (
                    <Card
                      key={lead.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, lead)}
                      onClick={() => setSelectedLead(lead)}
                      className={`cursor-pointer hover:border-primary/50 transition-all ${getActivityColor()}`}
                      data-testid={`lead-card-${lead.id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5 flex-wrap">
                              <p className="font-medium truncate">{lead.name}</p>
                              {lead.leadScore === 'hot' && (
                                <Badge className="text-[10px] px-1.5 py-0 bg-rose-100 text-rose-700 border-rose-200" title="Hot lead">
                                  🔥
                                </Badge>
                              )}
                              {lead.leadScore === 'warm' && (
                                <Badge className="text-[10px] px-1.5 py-0 bg-amber-100 text-amber-700 border-amber-200" title="Warm lead">
                                  🪵
                                </Badge>
                              )}
                              {lead.leadScore === 'cold' && (
                                <Badge variant="outline" className="text-[10px] px-1.5 py-0 text-sky-700 border-sky-200" title="Cold lead">
                                  ❄️
                                </Badge>
                              )}
                            </div>
                            <div className="mt-2 space-y-1 text-sm text-muted-foreground">
                              <div className="flex items-center gap-2">
                                <Phone className="h-3 w-3" />
                                <span className="truncate">{lead.mobile}</span>
                              </div>
                              {lead.city && (
                                <div className="flex items-center gap-2">
                                  <MapPin className="h-3 w-3" />
                                  <span className="truncate">{lead.city}</span>
                                </div>
                              )}
                            </div>
                          </div>
                          <GripVertical className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        </div>
                        <div className="mt-3 flex items-center justify-between">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="outline" className="text-xs">
                              {lead.leadSource}
                            </Badge>
                            {/* Total Calls Badge */}
                            <Badge 
                              variant="secondary" 
                              className={`text-xs ${(lead.totalCalls || 0) > 0 ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'}`}
                              title={`${lead.totalCalls || 0} total call attempts`}
                            >
                              <Phone className="h-3 w-3 mr-1" />
                              {lead.totalCalls || 0}
                            </Badge>
                            {/* CNR Badge - Only No Answer calls */}
                            {(lead.cnrCount || 0) > 0 && (
                              <Badge 
                                variant="secondary" 
                                className="text-xs bg-orange-100 text-orange-700"
                                title={`${lead.cnrCount} calls not received (CNR)`}
                              >
                                CNR: {lead.cnrCount}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            {/* Quick Call Button */}
                            {lead.mobile && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                onClick={(e) => handleQuickCall(e, lead)}
                                title="Quick Call"
                                data-testid={`call-${lead.id}`}
                              >
                                <PhoneCall className="h-4 w-4" />
                              </Button>
                            )}
                            {/* WhatsApp Button for relevant statuses */}
                            {['Virtual Meet', 'Awaited Visit'].includes(status) && lead.mobile && (
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 text-green-600 hover:text-green-700 hover:bg-green-50"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openWhatsApp(lead.mobile, getWhatsAppMessage(lead, status));
                                }}
                                data-testid={`whatsapp-${lead.id}`}
                              >
                                <MessageCircle className="h-4 w-4" />
                              </Button>
                            )}
                            {lead.assignedExecutiveName && (
                              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                <User className="h-3 w-3" />
                                <span className="truncate max-w-[60px]">
                                  {lead.assignedExecutiveName}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                    );
                  })}
                  {statusLeads.length === 0 && (
                    <div className="text-center py-8 text-sm text-muted-foreground">
                      No leads in this stage
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Create Lead Dialog */}
      <LeadDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onSuccess={handleLeadCreated}
        executives={executives}
      />

      {/* Lead Details Dialog */}
      {selectedLead && (
        <LeadDetailsDialog
          lead={selectedLead}
          open={!!selectedLead}
          onClose={() => setSelectedLead(null)}
          onUpdate={handleLeadUpdated}
          executives={executives}
        />
      )}

      {/* Status Change Dialog */}
      {statusChangeDialog.open && (
        <StatusChangeDialog
          open={statusChangeDialog.open}
          onClose={() => setStatusChangeDialog({ open: false, lead: null, targetStatus: null })}
          lead={statusChangeDialog.lead}
          targetStatus={statusChangeDialog.targetStatus}
          onSuccess={handleStatusChangeSuccess}
        />
      )}

      {/* Call Remark Dialog */}
      <CallRemarkDialog
        open={callRemarkDialog.open}
        onOpenChange={(open) => setCallRemarkDialog({ ...callRemarkDialog, open })}
        lead={callRemarkDialog.lead}
        onSubmit={handleCallLogSubmit}
        api={api}
      />

      {/* Next-Action Dialog — mandatory after intermediate status changes */}
      <NextActionDialog
        open={nextActionDialog.open}
        onClose={() => setNextActionDialog({ open: false, lead: null, statusContext: null })}
        lead={nextActionDialog.lead}
        statusContext={nextActionDialog.statusContext}
        onSaved={fetchLeads}
      />
    </div>
  );
};

export default LeadsBoardPage;
